import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  PiSquaresFour, PiSquaresFourFill, PiDrop, PiDropFill, PiThermometerSimple, PiThermometerSimpleFill,
  PiLightning, PiLightningFill, PiCamera, PiCameraFill,
  PiGear, PiGearFill, PiNotePencil, PiNotePencilFill, PiPlant, PiPlantFill, PiGridNine, PiGridNineFill,
  PiClipboardText, PiClipboardTextFill, PiImages, PiImagesFill, PiFlask, PiFlaskFill,
  PiShieldCheck, PiShieldCheckFill, PiCalendarBlank, PiCalendarBlankFill, PiWarningDiamond, PiWarningDiamondFill,
  PiShoppingCartSimple, PiShoppingCartSimpleFill,
  PiSun, PiMoon, PiList, PiX, PiSignOut, PiDotsThreeOutline, PiDotsThreeOutlineFill,
  PiSidebarSimple,
} from 'react-icons/pi';
import { statusColor } from '../config/sensorRanges';
import { useFarmStatus } from '../contexts/FarmStatusContext';
import { useSocket } from '../contexts/SocketContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

/*
 * The app shell: sidebar (desktop), header with section / page and the live
 * status, and on phones a bottom bar plus a drawer with everything else.
 *
 * Neutral chrome that follows the theme (white in light mode, dark in dark mode):
 * the colour belongs to the farm data and photos, not the frame. The current
 * page is a solid pill in the text colour, so it inverts with the theme.
 */

const SIDEBAR_W = 236;
const RAIL_W = 68;           // collapsed: icons only

/* ------------------------------------------------------------------ navigation */

const navSections = [
  {
    title: 'Overview',
    items: [
      { path: '/', label: 'Dashboard', icon: PiSquaresFour, iconOn: PiSquaresFourFill, exact: true },
    ],
  },
  {
    title: 'Monitor',
    items: [
      { path: '/hydro-units', label: 'Hydro units', icon: PiDrop, iconOn: PiDropFill },
      { path: '/rooms', label: 'Environment', icon: PiThermometerSimple, iconOn: PiThermometerSimpleFill },
      { path: '/power', label: 'Power', icon: PiLightning, iconOn: PiLightningFill },
      { path: '/cameras', label: 'Cameras', icon: PiCamera, iconOn: PiCameraFill },
    ],
  },
  {
    title: 'Farm work',
    items: [
      { path: '/manual-entry', label: 'Manual entry', icon: PiNotePencil, iconOn: PiNotePencilFill },
      { path: '/plant-log', label: 'Plant log', icon: PiPlant, iconOn: PiPlantFill },
      { path: '/plant-viewer', label: 'Plant viewer', icon: PiGridNine, iconOn: PiGridNineFill },
      { path: '/rack-photos', label: 'Rack photos', icon: PiImages, iconOn: PiImagesFill },
      { path: '/nutrients', label: 'Nutrients', icon: PiFlask, iconOn: PiFlaskFill },
      { path: '/spray-log', label: 'Spray log', icon: PiShieldCheck, iconOn: PiShieldCheckFill },
      { path: '/issues', label: 'Issues', icon: PiWarningDiamond, iconOn: PiWarningDiamondFill },
      { path: '/purchases', label: 'Purchases', icon: PiShoppingCartSimple, iconOn: PiShoppingCartSimpleFill },
      { path: '/manager-log', label: 'Manager log', icon: PiClipboardText, iconOn: PiClipboardTextFill },
      { path: '/calendar', label: 'Calendar', icon: PiCalendarBlank, iconOn: PiCalendarBlankFill },
    ],
  },
];

// Pinned to the bottom of the sidebar. Admin accounts only (App.js's AdminOnly turns
// others away from the URLs too). Data export, pH test and Activity are used rarely,
// so they are reached from the Settings page, and Settings stays lit while you are on them.
const SETTINGS_PAGES = ['/export', '/ph-test', '/activity'];
const utilityItems = [
  { path: '/settings', label: 'Settings', icon: PiGear, iconOn: PiGearFill, also: SETTINGS_PAGES },
];

// Bottom nav shows 4 items + More
const bottomNavItems = [
  { path: '/', label: 'Home', icon: PiSquaresFour, iconOn: PiSquaresFourFill, exact: true },
  { path: '/hydro-units', label: 'Units', icon: PiDrop, iconOn: PiDropFill },
  // The two things the manager logs every day; Plant Viewer and Cameras are in More.
  { path: '/manual-entry', label: 'Manual', icon: PiNotePencil, iconOn: PiNotePencilFill },
  { path: '/plant-log', label: 'Plant log', icon: PiPlant, iconOn: PiPlantFill },
];

const bottomPaths = bottomNavItems.map(i => i.path);

const isActivePath = (item, pathname) => (item.exact ? pathname === item.path
  : pathname.startsWith(item.path) || (item.also || []).some(p => pathname.startsWith(p)));

// The sidebar group a page sits in, for the header's "Monitor / Power" crumb.
function sectionOf(pathname) {
  if (pathname === '/') return null;
  if (SETTINGS_PAGES.some(p => pathname.startsWith(p))) return 'Settings';
  const all = [...navSections, { title: 'System', items: utilityItems }];
  const found = all.find(sec => sec.items.some(i => !i.exact && pathname.startsWith(i.path)));
  return found?.title || null;
}

function getPageTitle(pathname) {
  if (pathname === '/') return 'Dashboard';
  if (pathname === '/hydro-units') return 'Hydro units';
  if (pathname === '/settings') return 'Settings';
  if (pathname.startsWith('/hydro-units/')) {
    const unitId = pathname.split('/')[2];
    return `Unit ${unitId.toUpperCase()}`;
  }
  if (pathname === '/cameras') return 'Cameras';
  if (pathname === '/export') return 'Data export';
  if (pathname === '/rooms') return 'Environment';
  if (pathname === '/power') return 'Power';
  if (pathname === '/calendar') return 'Calendar';
  if (pathname === '/spray-log') return 'Spray log';
  if (pathname === '/nutrients') return 'Nutrients';
  if (pathname === '/manager-log') return 'Manager log';
  if (pathname === '/rack-photos') return 'Rack photos';
  if (pathname === '/issues') return 'Issues';
  if (pathname === '/purchases') return 'Purchases';
  if (pathname === '/issues/new') return 'Report a problem';
  if (pathname.startsWith('/issues/')) return `Issue #${pathname.split('/')[2]}`;
  if (pathname === '/plant-log') return 'Plant log';
  if (pathname === '/plant-viewer') return 'Plant viewer';
  if (pathname.startsWith('/plant/')) return 'Plant';
  if (pathname === '/manual-entry') return 'Manual entry';
  if (pathname === '/ph-test') return 'pH test';
  if (pathname === '/activity') return 'Activity';
  return 'NeuralKissan';
}


/* ------------------------------------------------------------------ shell */

const Container = styled.div`
  display: flex;
  height: 100vh;
  background: ${p => p.theme.colors.background};
  overflow: hidden;

  @media (max-width: ${p => p.theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const Main = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;

  @media (max-width: ${p => p.theme.breakpoints.md}) {
    padding-bottom: 64px; /* space for the bottom nav */
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 20px 24px 28px;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;

  @media (max-width: ${p => p.theme.breakpoints.md}) {
    padding: ${p => p.theme.spacing.md};
  }
`;

/* ------------------------------------------------------------------ brand */

/* The NK mark plus the name in the ordinary text colour: on a neutral sidebar
   the mark reads as the app icon, not as the name said twice. */
const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  min-height: 0;
  color: ${p => p.theme.colors.text};
  text-decoration: none;

  img {
    width: ${p => p.$size || 28}px;
    height: ${p => p.$size || 28}px;
    border-radius: ${p => Math.round((p.$size || 28) * 0.26)}px;
    display: block;
    flex: 0 0 auto;
  }
  .word { font-size: 15.5px; font-weight: 650; letter-spacing: -0.015em; line-height: 1; white-space: nowrap; }
  &:focus-visible { outline: 2px solid ${p => p.theme.colors.primary}; outline-offset: 3px; border-radius: 6px; }
`;

function BrandMark({ size, onClick, markOnly }) {
  return (
    <Brand to="/" title="Dashboard" $size={size} onClick={onClick} aria-label="NeuralKissan, go to the Dashboard">
      <img src="/logo.jpg" alt="" />
      {!markOnly && <span className="word">NeuralKissan</span>}
    </Brand>
  );
}

/* ------------------------------------------------------------------ sidebar */

const Sidebar = styled.nav`
  width: ${p => (p.$rail ? RAIL_W : SIDEBAR_W)}px;
  min-width: ${p => (p.$rail ? RAIL_W : SIDEBAR_W)}px;
  background: ${p => p.theme.colors.surface};
  border-right: 1px solid ${p => p.theme.colors.border};
  display: flex;
  flex-direction: column;
  transition: width 0.18s ease, min-width 0.18s ease;

  .top {
    display: flex; align-items: center; justify-content: space-between; gap: 6px;
    min-height: 56px;
    padding: 0 10px 0 16px;
    border-bottom: 1px solid ${p => p.theme.colors.border};
  }
  .top.rail { flex-direction: column; justify-content: center; gap: 8px; padding: 12px 0 10px; }
  .scroll { flex: 1; overflow-y: auto; overflow-x: hidden; padding: 0 12px 12px; }
  .bottom { padding: 8px 12px 12px; }

  @media (prefers-reduced-motion: reduce) { transition: none; }
  @media (max-width: ${p => p.theme.breakpoints.md}) { display: none; }
`;

const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${p => p.theme.colors.textMuted};
  padding: 20px 10px 8px;
`;

// Between groups in the rail (no room for names), and above the pinned items.
const Rule = styled.div`
  height: 1px;
  margin: ${p => p.$m || '10px 6px'};
  background: ${p => p.theme.colors.border};
`;

const NavList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 2px;
`;

/* Quiet grey until current; the current page is one surface step up with a hairline,
   its filled icon in the accent: the selection is the sidebar's only colour. */
const NavLink = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: ${p => (p.$rail ? 'center' : 'flex-start')};
  gap: 10px;
  min-height: 0;
  padding: ${p => (p.$rail ? '8px 0' : '8px 10px')};
  border-radius: 8px;
  color: ${p => (p.$active ? p.theme.colors.text : p.theme.colors.textSecondary)};
  background: ${p => (p.$active ? p.theme.colors.surfaceHover : 'transparent')};
  box-shadow: ${p => (p.$active ? `inset 0 0 0 1px ${p.theme.colors.borderHover}` : 'none')};
  text-decoration: none;
  font-size: 14px;
  font-weight: ${p => (p.$active ? 500 : 400)};
  transition: background-color 0.12s ease, color 0.12s ease;

  svg { flex: 0 0 auto; font-size: ${p => (p.$rail ? 20 : 18)}px; color: ${p => (p.$active ? p.theme.colors.primary : 'inherit')}; }
  .label { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

  &:hover {
    background: ${p => p.theme.colors.surfaceHover};
    color: ${p => p.theme.colors.text};
  }
  &:focus-visible { outline: 2px solid ${p => p.theme.colors.primary}; outline-offset: 1px; }
`;

/* A page's state, from FarmStatusContext. A count for problems to act on
   (warning / critical); otherwise a dot: the page's live state on Monitor pages,
   or a notice. In the rail both shrink to a dot on the icon's corner. */
const Badge = styled.span`
  flex: 0 0 auto;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: ${p => (p.$status === 'critical' ? '#fff' : '#1b1b1b')};
  background: ${p => statusColor(p.theme, p.$status)};
`;

const StateDot = styled.span`
  flex: 0 0 auto;
  width: 6px;
  height: 6px;
  margin-right: 6px;
  border-radius: 50%;
  background: ${p => (p.$status === 'offline' ? p.theme.colors.borderHover : statusColor(p.theme, p.$status))};
`;

const CornerDot = styled.span`
  position: absolute;
  top: 5px;
  right: 13px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => statusColor(p.theme, p.$status)};
  box-shadow: 0 0 0 2px ${p => p.theme.colors.surface};
`;

const Account = styled.div`
  display: flex;
  flex-direction: ${p => (p.$rail ? 'column' : 'row')};
  align-items: center;
  gap: ${p => (p.$rail ? 4 : 10)}px;
  padding: ${p => (p.$rail ? '10px 0 2px' : '10px 2px 2px 6px')};
  border-top: 1px solid ${p => p.theme.colors.border};
  margin-top: 8px;

  .avatar {
    width: 32px; height: 32px;
    border-radius: 50%;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 13px; font-weight: 500;
    color: #fff;
    background: ${p => p.theme.colors.primary};
    flex: 0 0 auto;
  }
  .who { flex: 1; min-width: 0; line-height: 1.25; }
  .who b { display: block; font-size: 13px; font-weight: 500; color: ${p => p.theme.colors.text}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .who span { font-size: 12px; color: ${p => p.theme.colors.textMuted}; text-transform: capitalize; }
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  min-height: 0;
  padding: 0;
  border: 1px solid ${p => (p.$boxed ? p.theme.colors.border : 'transparent')};
  border-radius: 8px;
  background: transparent;
  color: ${p => p.theme.colors.textSecondary};
  font-size: 18px;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;

  &:hover { background: ${p => p.theme.colors.surfaceHover}; color: ${p => p.theme.colors.text}; }
  &:focus-visible { outline: 2px solid ${p => p.theme.colors.primary}; outline-offset: 1px; }
`;

/* ------------------------------------------------------------------ header */

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 20px 0 24px;
  min-height: 56px;
  background: ${p => p.theme.colors.surface};
  border-bottom: 1px solid ${p => p.theme.colors.border};

  .crumb { display: flex; align-items: center; gap: 8px; min-width: 0; margin: 0; font-size: 14px; font-weight: 400; }
  .crumb .sec { color: ${p => p.theme.colors.textMuted}; }
  .crumb .sep { color: ${p => p.theme.colors.borderHover}; }
  .crumb .page { color: ${p => p.theme.colors.text}; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .right { margin-left: auto; display: flex; align-items: center; gap: 8px; }
  .menu, .mlogo { display: none; }

  @media (max-width: ${p => p.theme.breakpoints.md}) {
    padding: 0 10px 0 6px;
    min-height: 52px;
    gap: 6px;
    .crumb, .theme { display: none; }
    .menu, .mlogo { display: flex; }
  }
`;

/* Live status: one box instead of a loose dot, word and time. */
const Live = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.border};
  font-size: 12px;
  font-weight: 500;
  color: ${p => p.theme.colors.textSecondary};
  white-space: nowrap;

  .dot {
    width: 7px; height: 7px; border-radius: 50%;
    background: ${p => (p.$on ? p.theme.colors.success : p.theme.colors.warningHover)};
  }
  b { font-weight: 600; color: ${p => p.theme.colors.text}; }

  @media (max-width: ${p => p.theme.breakpoints.md}) {
    .time { display: none; }
    padding: 0 10px;
  }
`;

/* ------------------------------------------------------------------ mobile */

const BottomNav = styled.nav`
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${p => p.theme.colors.surface};
  border-top: 1px solid ${p => p.theme.colors.border};
  padding: 6px 8px;
  padding-bottom: max(6px, env(safe-area-inset-bottom));

  @media (max-width: ${p => p.theme.breakpoints.md}) {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 4px;
  }
`;

const Tab = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-height: 48px;
  padding: 4px 0;
  border: none;
  border-radius: 10px;
  background: ${p => (p.$active ? p.theme.colors.surfaceHover : 'transparent')};
  color: ${p => (p.$active ? p.theme.colors.text : p.theme.colors.textMuted)};
  font: inherit;
  font-size: 11px;
  font-weight: ${p => (p.$active ? 500 : 400)};
  text-decoration: none;
  cursor: pointer;

  svg { font-size: 21px; }
  &:active { transform: scale(0.96); }
`;

const DrawerOverlay = styled.div`
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1001;
  opacity: ${p => (p.$open ? 1 : 0)};
  visibility: ${p => (p.$open ? 'visible' : 'hidden')};
  transition: opacity 0.2s ease, visibility 0.2s ease;

  @media (max-width: ${p => p.theme.breakpoints.md}) { display: block; }
`;

const Drawer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 290px;
  max-width: 84vw;
  height: 100%;
  z-index: 1002;
  display: flex;
  flex-direction: column;
  background: ${p => p.theme.colors.surface};
  box-shadow: ${p => p.theme.shadows.lg};
  transform: translateX(${p => (p.$open ? '0' : '-100%')});
  transition: transform 0.22s ease;

  .head {
    display: flex; align-items: center; justify-content: space-between;
    min-height: 56px; padding: 0 10px 0 16px;
    border-bottom: 1px solid ${p => p.theme.colors.border};
  }
  .scroll { flex: 1; overflow-y: auto; padding: 6px 10px 10px; }
  .bottom { padding: 8px 10px calc(10px + env(safe-area-inset-bottom)); }

  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

/* ------------------------------------------------------------------ pieces */

function NavMarker({ entry, rail }) {
  if (!entry) return null;
  const { count, worst, state } = entry;
  const tone = count ? worst : (state && state !== 'normal' ? state : worst);
  if (rail) {
    return count || (tone && tone !== 'offline' && tone !== 'normal') ? <CornerDot $status={count ? worst : tone} /> : null;
  }
  if (count) return <Badge $status={worst} aria-label={`${count} to look at`}>{count}</Badge>;
  if (state) return <StateDot $status={state === 'normal' && worst === 'notice' ? 'notice' : state} title={state === 'offline' ? 'Not reporting' : 'Reporting'} />;
  if (worst === 'notice') return <StateDot $status="notice" />;
  return null;
}

function NavItems({ items, pathname, onNavigate, rail, nav }) {
  return (
    <NavList>
      {items.map((item) => {
        const active = isActivePath(item, pathname);
        const Icon = active ? item.iconOn : item.icon;
        const entry = nav?.[item.path];
        const hint = entry?.count ? `${item.label}: ${entry.count} to look at` : item.label;
        return (
          <li key={item.path}>
            <NavLink to={item.path} $active={active} $rail={rail} aria-current={active ? 'page' : undefined}
              onClick={onNavigate} title={rail ? hint : undefined} aria-label={rail ? hint : undefined}>
              <Icon aria-hidden="true" />
              {!rail && <span className="label">{item.label}</span>}
              <NavMarker entry={entry} rail={rail} />
            </NavLink>
          </li>
        );
      })}
    </NavList>
  );
}

function AccountRow({ user, isDarkMode, toggleTheme, logout, rail, withTheme }) {
  const name = user?.username || 'Signed in';
  return (
    <Account $rail={rail}>
      <span className="avatar" title={rail ? name : undefined} aria-hidden="true">{name.slice(0, 1).toUpperCase()}</span>
      {!rail && <span className="who"><b>{name}</b>{user?.role && <span>{user.role}</span>}</span>}
      {withTheme && (
        <IconButton onClick={toggleTheme} title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
          {isDarkMode ? <PiSun /> : <PiMoon />}
        </IconButton>
      )}
      <IconButton onClick={logout} title="Sign out" aria-label="Sign out"><PiSignOut /></IconButton>
    </Account>
  );
}

const Layout = ({ children }) => {
  const { pathname } = useLocation();
  // Both markers are `Date.now()` stamps set when a socket push lands, so the later
  // of the two is when the dashboard last heard from the farm.
  const { connected, sensorUpdateTS, roomUpdateTS } = useSocket();
  const lastPush = Math.max(sensorUpdateTS || 0, roomUpdateTS || 0);
  const { isDarkMode, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const utility = user?.role === 'admin' ? utilityItems : [];
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { nav } = useFarmStatus() || {};
  // Icon-only rail on desktop, remembered per browser.
  const [rail, setRail] = useState(() => localStorage.getItem('sidebarRail') === '1');
  const toggleRail = () => setRail((r) => { localStorage.setItem('sidebarRail', r ? '0' : '1'); return !r; });

  // Escape closes the drawer, like any other overlay.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = e => e.key === 'Escape' && setDrawerOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const close = () => setDrawerOpen(false);
  const section = sectionOf(pathname);
  // "More" is lit for any page that is not one of the four bottom tabs.
  const onMorePage = !bottomPaths.some(p => (p === '/' ? pathname === '/' : pathname.startsWith(p)));
  const synced = lastPush > 0
    ? new Date(lastPush).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase()
    : null;

  return (
    <Container>
      <Sidebar aria-label="Main" $rail={rail}>
        <div className={`top${rail ? ' rail' : ''}`}>
          <BrandMark markOnly={rail} size={rail ? 32 : 28} />
          <IconButton onClick={toggleRail} title={rail ? 'Expand menu' : 'Collapse menu'} aria-label={rail ? 'Expand menu' : 'Collapse menu'}>
            <PiSidebarSimple />
          </IconButton>
        </div>
        <div className="scroll">
          {navSections.map(section => (
            <div key={section.title}>
              {rail ? <Rule /> : <SectionTitle>{section.title}</SectionTitle>}
              <NavItems items={section.items} pathname={pathname} rail={rail} nav={nav} />
            </div>
          ))}
        </div>
        <div className="bottom">
          {utility.length > 0 && (
            <>
              <Rule $m="0 6px 8px" />
              <NavItems items={utility} pathname={pathname} rail={rail} nav={nav} />
            </>
          )}
          <AccountRow user={user} isDarkMode={isDarkMode} toggleTheme={toggleTheme} logout={logout} rail={rail} />
        </div>
      </Sidebar>

      <Main>
        <Header>
          <IconButton className="menu" onClick={() => setDrawerOpen(true)} aria-label="Open menu"><PiList /></IconButton>
          <span className="mlogo"><BrandMark size={26} /></span>
          <h1 className="crumb">
            {section && <><span className="sec">{section}</span><span className="sep" aria-hidden="true">/</span></>}
            <span className="page">{getPageTitle(pathname)}</span>
          </h1>
          <div className="right">
            <IconButton className="theme" $boxed onClick={toggleTheme}
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
              {isDarkMode ? <PiSun /> : <PiMoon />}
            </IconButton>
            <Live $on={connected} title={connected ? 'Receiving live sensor data' : 'Not receiving live sensor data'}>
              <span className="dot" />
              <b>{connected ? 'Live' : 'Offline'}</b>
              {synced && <span className="time">synced {synced}</span>}
            </Live>
          </div>
        </Header>
        <Content>{children}</Content>
      </Main>

      <DrawerOverlay $open={drawerOpen} onClick={close} />
      <Drawer $open={drawerOpen} aria-hidden={!drawerOpen}>
        <div className="head">
          <BrandMark onClick={close} />
          <IconButton onClick={close} aria-label="Close menu"><PiX /></IconButton>
        </div>
        <div className="scroll">
          {navSections.map(section => (
            <div key={section.title}>
              <SectionTitle>{section.title}</SectionTitle>
              <NavItems items={section.items} pathname={pathname} onNavigate={close} nav={nav} />
            </div>
          ))}
        </div>
        <div className="bottom">
          {utility.length > 0 && (
            <>
              <Rule $m="0 6px 8px" />
              <NavItems items={utility} pathname={pathname} onNavigate={close} nav={nav} />
            </>
          )}
          <AccountRow user={user} isDarkMode={isDarkMode} toggleTheme={toggleTheme} logout={logout} withTheme />
        </div>
      </Drawer>

      <BottomNav aria-label="Quick">
        {bottomNavItems.map((item) => {
          const active = isActivePath(item, pathname);
          const Icon = active ? item.iconOn : item.icon;
          return (
            <Tab key={item.path} to={item.path} $active={active} aria-current={active ? 'page' : undefined}>
              <Icon aria-hidden="true" />
              {item.label}
            </Tab>
          );
        })}
        <Tab as="button" type="button" $active={onMorePage} onClick={() => setDrawerOpen(true)} aria-label="More pages">
          {onMorePage ? <PiDotsThreeOutlineFill aria-hidden="true" /> : <PiDotsThreeOutline aria-hidden="true" />}
          More
        </Tab>
      </BottomNav>
    </Container>
  );
};

export default Layout;
